'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';

/**
 * Shown above main content when the signed-in user is suspended. Reads the
 * `suspended_until` from `/auth/me` and ticks a countdown every 30s. When
 * the timer hits zero we silently let it stay; the next API call will
 * auto-unsuspend (server-side in `require_active_user`) and `useAuth` will
 * refresh the user payload on its own cadence.
 */
// Backend ships naive UTC ISO strings (no Z suffix). Append Z so JS parses
// them as UTC, not local — otherwise a Bangkok user sees "expiring now"
// for a ban that's actually 7h away.
function parseUtc(iso: string): number {
  return new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
}

function fmtCountdown(until: string | null | undefined): string {
  if (!until) return 'indefinite — contact support to lift';
  const ms = parseUtc(until) - Date.now();
  if (ms <= 0) return 'expiring on your next action';
  const sec = Math.floor(ms / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h remaining`;
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

export default function SuspendedBanner() {
  const { user } = useAuth();
  const [, tick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!user || !user.is_suspended) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px',
        background: 'rgba(239, 68, 68, 0.08)',
        borderBottom: '1px solid rgba(239, 68, 68, 0.30)',
        color: 'var(--ink)',
        fontSize: 13,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#ef4444' }}>
        block
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>Your account is suspended.</b>{' '}
        <span style={{ color: 'var(--ink-2)' }}>
          You can still log in and read, but new prompts, edits, follows and reports are blocked. {fmtCountdown(user.suspended_until)}.
        </span>
        {user.suspended_reason && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            <b>Reason:</b> {user.suspended_reason}
          </div>
        )}
      </div>
    </div>
  );
}
